import '../../App.css';
import { useNavigate } from 'react-router-dom';
import backgroundImage from './assets/background.webp';
import botaoVamosLa from './assets/botaovamosla.webp';
import { useState, useEffect, useRef } from 'react';
import assetPreloader from '../../services/assetPreloader';
import OptimizedImage from '../../components/OptimizedImage';
import { enableRippleEffect } from '../../utils/mobileGestures';
import { makeAccessibleButton } from '../../utils/mobileAccessibility';

function Tela1_1() {
  const navigate = useNavigate();
  const [position, setPosition] = useState({ y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ y: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const imageRef = useRef(null);
  const lastTapTimeRef = useRef(0);
  const buttonRef = useRef(null);
  const loadTimeRef = useRef(Date.now());

  // Inicia o precarregamento de assets quando o componente montar
  useEffect(() => {
    // Inicia o carregamento com opção de background para não bloquear a UI
    assetPreloader.preloadAll(() => {
      console.log('Assets de alta prioridade carregados! Restante em segundo plano...');
      setIsLoading(false);
      // Atualiza o tempo de carregamento
      loadTimeRef.current = Date.now();
    }, { 
      background: true,  // Carregar média/baixa prioridade em segundo plano
      concurrency: 3     // Número máximo de carregamentos paralelos
    });
  }, []);
  
  // Aplica efeito ripple e acessibilidade ao botão
  useEffect(() => {
    if (buttonRef.current) {
      // Adicionar efeito ripple
      const ripple = enableRippleEffect(buttonRef.current, {
        color: 'rgba(76, 255, 0, 0.3)',
        centered: false
      });
      
      // Adicionar melhorias de acessibilidade
      makeAccessibleButton(buttonRef.current, {
        label: 'Vamos lá',
        description: 'Iniciar a experiência do aplicativo'
      });
      
      return () => {
        if (ripple) ripple.destroy();
      };
    }
  }, []);

  // Manipuladores de eventos para mouse
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartPos({ y: e.clientY - position.y });
    setHasInteracted(true);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const dragDistance = e.clientY - startPos.y;
      // Aplicando resistência - quanto mais longe do centro, menor o movimento
      const resistance = Math.abs(dragDistance) / 100 + 1;
      const newY = dragDistance / resistance;
      setPosition({ y: newY });
      setHasInteracted(true);
    }
  };

  const handleMouseUp = (e) => {
    setIsDragging(false);
    returnToCenter();
    
    // Verificar se houve tempo suficiente desde o carregamento da tela
    const timeSinceLoad = Date.now() - loadTimeRef.current;
    
    // Só considera clique válido se houve interação real e tempo suficiente
    if (Math.abs(position.y) < 10 && hasInteracted && timeSinceLoad > 500) {
      navigateToScreen2();
    }
  };

  // Manipuladores de eventos para toque (dispositivos móveis)
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartPos({ y: e.touches[0].clientY - position.y });
    setHasInteracted(true);
  };

  const handleTouchMove = (e) => {
    if (isDragging) {
      const dragDistance = e.touches[0].clientY - startPos.y;
      // Aplicando resistência - quanto mais longe do centro, menor o movimento
      const resistance = Math.abs(dragDistance) / 80 + 1;
      const newY = dragDistance / resistance;
      setPosition({ y: newY });
      setHasInteracted(true);
    }
  };

  const handleTouchEnd = (e) => {
    setIsDragging(false);
    returnToCenter();
    
    // Verificar se houve tempo suficiente desde o carregamento da tela
    const timeSinceLoad = Date.now() - loadTimeRef.current;
    
    // Verificar se foi um tap simples (não um arrasto)
    if (Math.abs(position.y) < 10 && hasInteracted && timeSinceLoad > 500) {
      const now = Date.now();
      const timeSince = now - lastTapTimeRef.current;
      
      // Evitar duplo tap
      if (timeSince > 300) {
        navigateToScreen2();
      }
      
      lastTapTimeRef.current = now;
    }
  };

  // Navegação para a tela 2
  const navigateToScreen2 = () => {
    // Só permitir a navegação se os assets já estiverem carregados
    if (!isLoading) {
      navigate('/tela2');
    }
  };

  // Retorna a imagem ao centro com animação rápida de elástico
  const returnToCenter = () => {
    if (imageRef.current) {
      imageRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      
      setTimeout(() => {
        setPosition({ y: 0 });
        
        // Remove a transição após a animação completar
        setTimeout(() => {
          if (imageRef.current) {
            imageRef.current.style.transition = '';
          }
        }, 300);
      }, 10);
    }
  };

  // Manipulador seguro para o botão
  const handleButtonClick = () => {
    // Verificar se houve tempo suficiente desde o carregamento da tela
    const timeSinceLoad = Date.now() - loadTimeRef.current;
    
    if (!isLoading && timeSinceLoad > 500) {
      navigate('/tela2');
    }
  };

  // Adiciona manipuladores de eventos globais
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        returnToCenter();
      }
    };

    // Adiciona listeners globais para garantir que o mouseup seja detectado mesmo fora do componente
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchend', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchend', handleGlobalMouseUp);
    };
  }, [isDragging]);

  // Calcular o valor de 5% da altura da tela
  const offsetY = window.innerHeight * 0.05;

  return (
    <div className="App">
      <div className="background-container">
        <OptimizedImage 
          src={backgroundImage} 
          className="background-image" 
          alt="Background" 
          loading="eager"
          responsive={false}
          fullWidth={true}
        />
      </div>
      
      {/* Fixed button container */}
      <div className="fixed-content" style={{ 
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 3,
        pointerEvents: 'none',
        width: '100%'
      }}>
        <div
          ref={buttonRef}
          className="button-container touch-feedback interactive-element"
          style={{
            maxWidth: '60%',
            pointerEvents: 'auto',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden'
          }}
          onClick={handleButtonClick}
          role="button"
          tabIndex={0}
        >
          <OptimizedImage 
            src={botaoVamosLa}
            alt="Vamos lá!"
            style={{
              marginTop: '490px',
              width: '100%',
              height: 'auto'
            }}
            loading="eager"
            responsive={true}
          />
        </div>
      </div>
      
      {/* Interactive container com o mesmo estilo da Tela1 */}
      <div 
        className="central-image-container interactive-element"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        tabIndex={0}
        style={{ zIndex: 10 }}
      >
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transform: `translateY(calc(${position.y}px + ${offsetY}px))`,
          paddingTop: '5vh'
        }}>
          {/* Container vazio para detecção de arrasto mas com o mesmo comportamento da Tela1 */}
        </div>
      </div>
    </div>
  );
}

export default Tela1_1; 